import React, { useState, useCallback } from 'react';
import { Upload, MapPin, X, Check, AlertCircle, Camera, Zap } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export default function ImageUploadModal({ isOpen, onClose, initialCoordinates, propertyUrl }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [coordinates, setCoordinates] = useState(initialCoordinates || { lat: '', lon: '' });
    const [uploading, setUploading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [pointsEarned, setPointsEarned] = useState(0);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        if (selectedFile.size > 10 * 1024 * 1024) {
            setError('Image must be less than 10MB');
            return;
        }

        setFile(selectedFile);
        setError(null);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileChange({ target: { files: [droppedFile] } });
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleUpload = async () => {
        if (!file) {
            setError('Please select an image');
            return;
        }

        if (!coordinates.lat || !coordinates.lon) {
            setError('Please confirm the coordinates');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            const formData = new FormData();
            formData.append('image', file);
            formData.append('latitude', coordinates.lat);
            formData.append('longitude', coordinates.lon);
            if (propertyUrl) {
                formData.append('propertyUrl', propertyUrl);
            }

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/api/proprpoints/upload`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                setSuccess(true);
                setPointsEarned(response.data.image.pointsAwarded);

                // Auto-close after 3 seconds
                setTimeout(() => {
                    onClose();
                    resetForm();
                }, 3000);
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.response?.data?.error || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setFile(null);
        setPreview(null);
        setCoordinates({ lat: '', lon: '' });
        setSuccess(false);
        setError(null);
        setPointsEarned(0);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <Camera className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Contribute to VPR Database</h2>
                            <p className="text-sm text-gray-500">Help improve location accuracy</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close modal"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {!success ? (
                        <>
                            {/* Upload Area */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Upload Property Image
                                </label>
                                {!preview ? (
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer"
                                        onClick={() => document.getElementById('file-upload').click()}
                                    >
                                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-600 mb-2">
                                            <span className="font-semibold text-emerald-600">Click to upload</span> or drag and drop
                                        </p>
                                        <p className="text-sm text-gray-500">PNG, JPG up to 10MB</p>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <img
                                            src={preview}
                                            alt="Preview"
                                            className="w-full h-64 object-cover rounded-xl"
                                        />
                                        <button
                                            onClick={() => {
                                                setFile(null);
                                                setPreview(null);
                                            }}
                                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Coordinates */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    <MapPin className="w-4 h-4 inline mr-1" />
                                    Confirm Location
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Latitude</label>
                                        <input
                                            type="number"
                                            step="0.000001"
                                            value={coordinates.lat}
                                            onChange={(e) => setCoordinates({ ...coordinates, lat: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            placeholder="38.7223"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">Longitude</label>
                                        <input
                                            type="number"
                                            step="0.000001"
                                            value={coordinates.lon}
                                            onChange={(e) => setCoordinates({ ...coordinates, lon: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            placeholder="-9.1393"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    These coordinates are pre-filled from the analysis. Adjust if needed.
                                </p>
                            </div>

                            {/* Rewards Info */}
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                    <Zap className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-emerald-900">Earn ProprPoints!</p>
                                        <p className="text-sm text-emerald-700 mt-1">
                                            Get <span className="font-bold">50+ points</span> for each verified image you contribute.
                                            Your images help improve location accuracy for everyone!
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            )}

                            {/* Upload Button */}
                            <button
                                onClick={handleUpload}
                                disabled={uploading || !file || !coordinates.lat || !coordinates.lon}
                                className="w-full px-6 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {uploading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-5 h-5" />
                                        Upload & Earn Points
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        /* Success State */
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-10 h-10 text-emerald-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Upload Successful!</h3>
                            <p className="text-gray-600 mb-4">
                                You earned <span className="font-bold text-emerald-600">{pointsEarned} ProprPoints</span>
                            </p>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 inline-block">
                                <p className="text-sm text-emerald-800">
                                    Your image has been added to the VPR database and will help improve location accuracy!
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
